import React, { useEffect, useState } from 'react';
import {
  ComparisonFilters,
  ComparisonLineItem,
  ComparisonRow,
  ComparisonWidget,
  GroupByDimension,
  comparisonDashboardService,
} from '../../services/comparisonDashboardService';
import {
  display_group_label,
  drill_first_column_label,
  group_list_rows,
  ROLLUP_HIDDEN_METRIC_KEYS,
  rows_for_group,
  widget_for_key,
} from '../../utils/comparisonBreakdownDrilldown';
import {
  breakdown_composition_csv,
  breakdown_list_csv,
  download_csv,
  line_items_csv,
} from '../../utils/comparisonBreakdownExport';
import {
  COMPARISON_GROUP_BY_OPTIONS,
  COMPARISON_METRIC_COLUMNS,
  COMPARISON_SECONDARY_LABEL,
  formatComparisonMetricValue,
} from './comparisonBreakdownConstants';
import ExportButton from './ExportButton';

const styles: Record<string, React.CSSProperties> = {
  groupByRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0 0 0.75rem',
    flexWrap: 'wrap' as const,
  },
  groupByLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'var(--report-text)',
  },
  groupByButtons: {
    display: 'flex',
    gap: '0.35rem',
    flexWrap: 'wrap' as const,
  },
  groupByBtn: {
    padding: '0.4rem 0.85rem',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    background: 'var(--report-surface)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--report-text)',
    transition: 'all 0.15s',
  },
  groupByBtnActive: {
    background: 'var(--report-accent)',
    color: 'white',
  },
  loadingBox: {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--report-text-muted)',
    fontSize: '0.95rem',
  },
  truncatedNotice: {
    marginBottom: '0.75rem',
    padding: '0.65rem 0.85rem',
    background: 'var(--report-surface-subtle)',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    color: 'var(--report-text-muted)',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  loadMoreBtn: {
    padding: '0.35rem 0.75rem',
    border: '1px solid var(--report-border)',
    borderRadius: '6px',
    background: 'var(--report-surface)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--report-text)',
    whiteSpace: 'nowrap' as const,
  },
  widgetCard: {
    background: 'var(--report-surface)',
    border: '1px solid var(--report-border)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  widgetHeader: {
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '1.05rem',
    color: 'var(--report-text)',
    borderBottom: '1px solid var(--report-border)',
    background: 'var(--report-surface-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    padding: '0.5rem 0.65rem',
    textAlign: 'right' as const,
    fontWeight: 600,
    color: 'var(--report-text-muted)',
    borderBottom: '2px solid var(--report-border)',
    whiteSpace: 'nowrap' as const,
    fontSize: '0.8rem',
  },
  thFirst: {
    textAlign: 'left' as const,
  },
  td: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
  },
  tdNum: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid var(--report-border)',
    color: 'var(--report-text)',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  breadcrumbLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--report-accent)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  chevron: {
    marginLeft: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--report-accent)',
  },
  hint: {
    padding: '0.5rem 1rem',
    fontSize: '0.8rem',
    color: 'var(--report-text-muted)',
  },
  itemNum: {
    fontVariantNumeric: 'tabular-nums' as const,
    color: 'var(--report-text-muted)',
    fontWeight: 400,
  },
  errorBox: {
    margin: '0.75rem 1rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'var(--report-error-bg)',
    color: 'var(--report-error-text)',
    borderRadius: '6px',
    border: '1px solid var(--report-error-border)',
    fontSize: '0.9rem',
  },
};

const ComparisonMetricTable: React.FC<{
  name_column_label: string;
  rows: ComparisonRow[];
  on_row_click?: (row: ComparisonRow) => void;
  hide_average_columns?: boolean;
}> = ({ name_column_label, rows, on_row_click, hide_average_columns }) => {
  const columns = hide_average_columns
    ? COMPARISON_METRIC_COLUMNS.filter(
        (col) => !ROLLUP_HIDDEN_METRIC_KEYS.includes(col.key as (typeof ROLLUP_HIDDEN_METRIC_KEYS)[number])
      )
    : COMPARISON_METRIC_COLUMNS;

  return (
  <div style={styles.tableWrapper}>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={{ ...styles.th, ...styles.thFirst }}>Item #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>{name_column_label}</th>
          {columns.map((col) => (
            <th key={col.key} style={styles.th}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${row.row_key}::${row.row_label}::${index}`}
            onClick={on_row_click ? () => on_row_click(row) : undefined}
            style={on_row_click ? { cursor: 'pointer' } : undefined}
            onMouseEnter={
              on_row_click
                ? (e) => {
                    e.currentTarget.style.backgroundColor = 'var(--report-row-hover)';
                  }
                : undefined
            }
            onMouseLeave={
              on_row_click
                ? (e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                : undefined
            }
          >
            <td style={{ ...styles.td, ...styles.itemNum }}>{row.row_key || '—'}</td>
            <td style={{ ...styles.td, fontWeight: 500 }}>
              {display_group_label(row.row_key, row.row_label)}
              {on_row_click && <span style={styles.chevron}>▶</span>}
            </td>
            {columns.map((col) => (
              <td key={col.key} style={styles.tdNum}>
                {formatComparisonMetricValue(
                  (row as unknown as Record<string, number>)[col.key],
                  col.format
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const LineItemsTable: React.FC<{ rows: ComparisonLineItem[] }> = ({ rows }) => (
  <div style={styles.tableWrapper}>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={{ ...styles.th, ...styles.thFirst }}>Date</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Invoice #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Customer #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Customer</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Item #</th>
          <th style={{ ...styles.th, ...styles.thFirst }}>Name</th>
          <th style={styles.th}>Qty</th>
          <th style={styles.th}>Revenue</th>
          <th style={styles.th}>Unit Price</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.invoice_number}-${row.item_number}-${index}`}>
            <td style={styles.td}>{row.date}</td>
            <td style={styles.td}>{row.invoice_number}</td>
            <td style={{ ...styles.td, ...styles.itemNum }}>{row.customer_number}</td>
            <td style={styles.td}>{row.customer_name}</td>
            <td style={{ ...styles.td, ...styles.itemNum }}>{row.item_number}</td>
            <td style={styles.td}>{row.item_name}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.qty, 'number')}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.revenue, 'currency')}</td>
            <td style={styles.tdNum}>{formatComparisonMetricValue(row.unit_price, 'currency')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function export_filename(prefix: string, filters?: ComparisonFilters): string {
  const start = filters?.start_date || 'start';
  const end = filters?.end_date || 'end';
  return `${prefix}-${start}-to-${end}.csv`;
}

const DimensionDrilldownCard: React.FC<{
  groupBy: GroupByDimension;
  widgets: ComparisonWidget[];
  comparisonFilters?: ComparisonFilters;
}> = ({ groupBy, widgets, comparisonFilters }) => {
  const [drilled_key, set_drilled_key] = useState<string | null>(null);
  const [line_item_row, set_line_item_row] = useState<ComparisonRow | null>(null);
  const [line_items, set_line_items] = useState<ComparisonLineItem[]>([]);
  const [line_items_loading, set_line_items_loading] = useState(false);
  const [line_items_error, set_line_items_error] = useState<string | null>(null);
  const [line_items_truncated, set_line_items_truncated] = useState(false);

  const group_label =
    COMPARISON_GROUP_BY_OPTIONS.find((opt) => opt.value === groupBy)?.label ?? 'Group';
  const secondary_label = COMPARISON_SECONDARY_LABEL[groupBy];

  useEffect(() => {
    if (drilled_key && !widgets.some((widget) => widget.group_key === drilled_key)) {
      set_drilled_key(null);
      set_line_item_row(null);
    }
  }, [widgets, drilled_key]);

  useEffect(() => {
    if (!line_item_row || !comparisonFilters || !drilled_key) {
      set_line_items([]);
      set_line_items_truncated(false);
      set_line_items_error(null);
      return;
    }

    let cancelled = false;
    set_line_items_loading(true);
    set_line_items_error(null);

    void (async () => {
      try {
        const data = await comparisonDashboardService.getLineItems(
          comparisonFilters,
          groupBy,
          drilled_key,
          line_item_row.row_key
        );
        if (cancelled) return;
        set_line_items(data.rows);
        set_line_items_truncated(data.truncated);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load invoice lines';
        set_line_items_error(message);
        set_line_items([]);
      } finally {
        if (!cancelled) set_line_items_loading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [line_item_row, comparisonFilters, drilled_key, groupBy]);

  const drilled_widget = widget_for_key(widgets, drilled_key);
  const rows = drilled_widget
    ? rows_for_group(widgets, drilled_widget.group_key)
    : group_list_rows(widgets);
  const name_column_label = drill_first_column_label(drilled_key, group_label, secondary_label);

  const handle_row_click = (row: ComparisonRow) => {
    if (!drilled_widget) {
      set_drilled_key(row.row_key);
      return;
    }
    if (comparisonFilters) {
      set_line_item_row(row);
    }
  };

  const handle_download = () => {
    if (line_item_row) {
      download_csv(
        export_filename(`breakdown-lines-${line_item_row.row_key}`, comparisonFilters),
        line_items_csv(line_items, line_items_truncated)
      );
      return;
    }
    if (drilled_widget) {
      download_csv(
        export_filename(`breakdown-${groupBy}-${drilled_widget.group_key}`, comparisonFilters),
        breakdown_list_csv(rows)
      );
      return;
    }
    download_csv(
      export_filename(`breakdown-${groupBy}`, comparisonFilters),
      breakdown_composition_csv(widgets)
    );
  };

  const header_label = line_item_row
    ? `${line_item_row.row_key} ${line_item_row.row_label}`
    : drilled_widget
      ? drilled_widget.group_label
      : group_label;

  const hint = line_item_row
    ? 'Invoice lines that make up this total.'
    : drilled_widget
      ? 'Click a row to see the invoice lines behind that number.'
      : 'Click a row to see what makes up that number.';

  return (
    <div>
      {(drilled_widget || line_item_row) && (
        <div style={styles.breadcrumb}>
          <button
            type="button"
            style={styles.breadcrumbLink}
            onClick={() => {
              set_line_item_row(null);
              set_drilled_key(null);
            }}
          >
            {group_label}
          </button>
          {drilled_widget && (
            <>
              <span style={{ color: 'var(--report-text-muted)' }}>›</span>
              {line_item_row ? (
                <button
                  type="button"
                  style={styles.breadcrumbLink}
                  onClick={() => set_line_item_row(null)}
                >
                  {drilled_widget.group_label}
                </button>
              ) : (
                <span style={{ color: 'var(--report-text-muted)' }}>{drilled_widget.group_label}</span>
              )}
            </>
          )}
          {line_item_row && (
            <>
              <span style={{ color: 'var(--report-text-muted)' }}>›</span>
              <span style={{ color: 'var(--report-text-muted)' }}>
                {line_item_row.row_key} {line_item_row.row_label}
              </span>
            </>
          )}
        </div>
      )}
      <div style={styles.widgetCard}>
        <div style={styles.widgetHeader}>
          <span>{header_label}</span>
          <ExportButton
            onClick={handle_download}
            disabled={line_item_row ? line_items_loading || line_items.length === 0 : rows.length === 0}
            size="small"
            title="Download this list (CSV)"
          />
        </div>
        <div style={styles.hint}>{hint}</div>
        {line_item_row ? (
          line_items_loading ? (
            <div style={styles.loadingBox}>Loading invoice lines...</div>
          ) : line_items_error ? (
            <div style={styles.errorBox}>{line_items_error}</div>
          ) : line_items.length === 0 ? (
            <div style={styles.hint}>No invoice lines for this item.</div>
          ) : (
            <>
              {line_items_truncated && (
                <div style={styles.hint}>Showing the first 500 invoice lines.</div>
              )}
              <LineItemsTable rows={line_items} />
            </>
          )
        ) : (
          <ComparisonMetricTable
            name_column_label={name_column_label}
            rows={rows}
            on_row_click={handle_row_click}
            hide_average_columns={!drilled_widget}
          />
        )}
      </div>
    </div>
  );
};

export interface ComparisonBreakdownGridProps {
  groupBy: GroupByDimension;
  onGroupByChange: (gb: GroupByDimension) => void;
  widgets: ComparisonWidget[];
  loading: boolean;
  emptyMessage?: string;
  comparisonFilters?: ComparisonFilters;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/**
 * Group-by toolbar and one drilldown table (group list, child rows, then invoice lines).
 */
const ComparisonBreakdownGrid: React.FC<ComparisonBreakdownGridProps> = ({
  groupBy,
  onGroupByChange,
  widgets,
  loading,
  emptyMessage = 'No data found for the selected filters.',
  comparisonFilters,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
}) => (
  <>
    <div style={styles.groupByRow}>
      <span style={styles.groupByLabel}>Group by</span>
      <div style={styles.groupByButtons}>
        {COMPARISON_GROUP_BY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onGroupByChange(opt.value)}
            style={{
              ...styles.groupByBtn,
              ...(groupBy === opt.value ? styles.groupByBtnActive : {}),
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>

    {loading ? (
      <div style={styles.loadingBox}>Loading comparison data...</div>
    ) : widgets.length === 0 ? (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--report-text-muted)' }}>
        {emptyMessage}
      </div>
    ) : (
      <>
        {hasMore && (
          <div style={styles.truncatedNotice}>
            <span>
              Showing {widgets.length} groups by revenue. Add a filter to narrow, or load more.
            </span>
            {onLoadMore && (
              <button
                type="button"
                style={styles.loadMoreBtn}
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
        <DimensionDrilldownCard
          key={groupBy}
          groupBy={groupBy}
          widgets={widgets}
          comparisonFilters={comparisonFilters}
        />
      </>
    )}
  </>
);

export default ComparisonBreakdownGrid;
